import { Fragment, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { __MANAGEMENT_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { noop } from 'shell/utils/noop';
import { Operator } from 'shell/models/operator';
import FormOperator from 'system/components/operators/form';
import { OperatorForm } from 'system/view-models/operator';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { transformerOperator } from '../utils/transformer-operator';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { AxiosResponse } from 'axios';

interface OperatorDetailProps {
  onCancel?: () => void;
  id?: string;
}

const OperatorDetail = ({ onCancel, id }: OperatorDetailProps): React.ReactElement => {
  const params = useParams();

  const { userProfile } = useUserProfile();

  const [operator] = useHttpFetch<HttpResponse<Operator>, OperatorForm>(
    `${__MANAGEMENT_API_URL__}/operatori/${id || params.id}`,
    {
      onBootstrap: true,
      options: {
        errorCatching: true,
        method: 'GET',
        loading: true,
        data: [],
      },
      transformer: (response: AxiosResponse<HttpResponse<Operator>>) =>
        transformerOperator(response),
    }
  );

  const initialValues: OperatorForm | undefined = useMemo(() => {
    if (operator.isLoading || operator.pristine || userProfile.isLoading) {
      return undefined;
    }

    const anpr = userProfile.datiLogin?.profile?.feature?.anpr || '';

    const isRequiredAnpr = anpr === '1' || anpr === '2';

    return {
      ...((operator.data as OperatorForm) || {}),
      anpr: {
        ...((operator.data as OperatorForm)?.anpr || {}),
        isRequiredAnpr,
        isEditAnprData:
          (operator.data as OperatorForm)?.anpr?.anprOperatorCode ||
          (operator.data as OperatorForm)?.anpr?.validityStart ||
          (operator.data as OperatorForm)?.anpr?.validityEnd ||
          (operator.data as OperatorForm)?.anpr?.stationIdAnpr
            ? true
            : false,
      },
    } as OperatorForm;
  }, [operator.data, operator.isLoading, operator.pristine, userProfile.isLoading]);

  return (
    <Fragment>
      {operator.isLoading || operator.pristine || operator.hasErrors ? undefined : (
        <FormOperator
          title="Dettaglio Operatore"
          onOk={noop}
          onCancel={() => {
            if (onCancel) {
              onCancel();
            }
          }}
          disabled
          isDetail
          initialValues={initialValues}
        />
      )}
    </Fragment>
  );
};

OperatorDetail.displayName = 'Operator Detail component';

export default OperatorDetail;
